import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  isValidLanguage, 
  DEFAULT_LANGUAGE, 
  initializeLanguageFromURL,
  getBrowserLanguage,
  storeLanguagePreference,
  getStoredLanguagePreference
} from '../../utils/languageUtils';
import i18n from '../../i18n/i18n';

/**
 * Wrapper component that handles language routing and redirects
 */
const LanguageRouteWrapper = ({ children }) => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If no language in URL or invalid language, redirect to preferred language
    if (!lang || !isValidLanguage(lang)) {
      const preferredLang = getBrowserLanguage();
      const targetLang = lang && !isValidLanguage(lang) ? DEFAULT_LANGUAGE : preferredLang;
      
      // Remove invalid language from path and add correct one
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const pathWithoutLang = pathSegments.slice(1).join('/');
      const newPath = `/${targetLang}${pathWithoutLang ? `/${pathWithoutLang}` : ''}`;
      
      navigate(newPath, { replace: true });
      return;
    }

    // Valid language detected in URL
    // Store this as user preference (they navigated to this language intentionally)
    const storedPreference = getStoredLanguagePreference();
    if (lang !== storedPreference) {
      storeLanguagePreference(lang);
    }

    // Update i18n if needed
    if (lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [lang, navigate, location.pathname]);

  // Don't render children if we're redirecting
  if (!lang || !isValidLanguage(lang)) {
    return null;
  }

  return children;
};

export default LanguageRouteWrapper;
